import React from 'react';
import { Btn } from './index';
import { api } from '../lib/api';
import { useOptionalAuth } from '../contexts/AuthContext';
import { palette as T, type } from '../design';

const FONT = "'Plus Jakarta Sans', sans-serif";

// "Notify me" on an instructor's profile sidebar, always rendered: a student can follow an
// instructor whether or not a seat is open today, and gets one email per publish.
// A visitor is sent to sign in first. Instructors and admins do not follow, so they see nothing.
export function NotifyMeButton({ instructor, setPage }) {
  // Optional: rendered standalone (no auth provider) it shows nothing rather than guess who is
  // viewing. The app always renders it inside the provider.
  const auth = useOptionalAuth();
  const { isAuthenticated = false, isStudent = false, loading = false } = auth || {};
  const instructorId = instructor?.id;
  const name = instructor?.displayName || 'this instructor';
  const [following, setFollowing] = React.useState(false);
  const [ready, setReady] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (!isStudent || !instructorId) return undefined;
    let live = true;
    api.get('/follows/mine')
      .then(data => { if (live) setFollowing((data?.instructorIds || []).includes(instructorId)); })
      .catch(() => { /* Unknown state reads as not following; pressing the button still works. */ })
      .finally(() => { if (live) setReady(true); });
    return () => { live = false; };
  }, [isStudent, instructorId]);

  if (!auth || loading || !instructorId) return null;
  if (isAuthenticated && !isStudent) return null;

  const toggle = async () => {
    if (!isAuthenticated) {
      setPage('login');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (following) {
        await api.delete(`/follows/${encodeURIComponent(instructorId)}`);
        setFollowing(false);
      } else {
        await api.post('/follows', { instructorId });
        setFollowing(true);
      }
    } catch (err) {
      setError(err?.data?.message || 'We could not save your choice. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="notify-me" style={{ marginTop: 16, textAlign: 'center' }}>
      <Btn
        variant={following ? 'subtle' : 'ghost'}
        onClick={toggle}
        disabled={saving || (isStudent && !ready)}
        style={{ width: '100%', justifyContent: 'center' }}
      >
        {following ? 'Stop notifying me' : 'Notify me'}
      </Btn>
      <p style={{ fontFamily: FONT, fontSize: type.label, color: following ? T.walnut : T.driftwood, margin: '8px 0 0', lineHeight: 1.5 }}>
        {following
          ? `You get an email when ${name} publishes a session.`
          : `Get an email when ${name} publishes a session.`}
      </p>
      {error && (
        <p role="alert" style={{ fontFamily: FONT, fontSize: type.label, color: T.errorRed, margin: '6px 0 0' }}>{error}</p>
      )}
    </div>
  );
}
